import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import HomeIcon from '@mui/icons-material/Home';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import BathtubIcon from '@mui/icons-material/Bathtub';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import BusinessIcon from '@mui/icons-material/Business';
import FlipIcon from '@mui/icons-material/Flip';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import DevicesIcon from '@mui/icons-material/Devices';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { DataService } from 'src/services/data-service';
import UnitCreateStandaloneDialog from './UnitCreateStandaloneDialog';

// Dummy unit images - you can replace these with actual images
const DUMMY_IMAGES = [
  'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1484154218962-a197022b5858?w=400&h=300&fit=crop',
  'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=400&h=300&fit=crop'
];

interface FeeType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface UnitFeeConfig {
  id: string;
  feeType: FeeType;
  feeTypeId: string;
  daily: number;
  weekly: number;
  monthly: number;
  bimonthly: number;
  quarterly: number;
  semiAnnual: number;
  annual: number;
  oneTime: number;
}

interface Property {
  id: string;
  code: string;
  floor: number;
  contact: string;
}

interface UnitAppliance {
  id: string;
  quantity: number;
  typeId: string;
  type: {
    id: string;
    name: string;
    description: string;
  } | null;
}

interface Unit {
  id: string;
  number: string;
  rooms: number;
  bathrooms: number;
  floor: number;
  areaM2: number;
  property: Property;
  propertyId: string;
  unitFeeConfigs: UnitFeeConfig[];
}

// Styled flip card container - NO hover effects
const FlipCardContainer = styled(Box)(({ theme }) => ({
  perspective: '1000px',
  height: '100%',
  minHeight: '600px',
  position: 'relative'
}));

const FlipCardInner = styled(Box)<{ flipped: boolean }>(({ theme, flipped }) => ({
  position: 'relative',
  width: '100%',
  height: '100%',
  textAlign: 'center',
  transition: 'transform 0.5s ease-in-out',
  transformStyle: 'preserve-3d',
  transform: flipped ? 'rotateY(180deg)' : 'rotateY(0deg)'
}));

const FlipCardFace = styled(Card)(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '100%',
  backfaceVisibility: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: theme.shadows[8],
  borderRadius: theme.spacing(2),
  overflow: 'hidden',
  pointerEvents: 'auto',
  transition: 'none !important',
  '&:hover': {
    boxShadow: theme.shadows[8],
    transform: 'none !important'
  }
}));

const FlipCardFront = styled(FlipCardFace)({
  '&:hover': {
    transform: 'none !important'
  }
});

const FlipCardBack = styled(FlipCardFace)({
  transform: 'rotateY(180deg)',
  pointerEvents: 'auto',
  '&:hover': {
    transform: 'rotateY(180deg) !important'
  }
});

const StyledCardMedia = styled(CardMedia)(({ theme }) => ({
  height: 240,
  position: 'relative',
  backgroundSize: 'cover',
  backgroundPosition: 'center'
}));

const RentalUnitsListing: React.FC = () => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flippedCards, setFlippedCards] = useState<{ [key: string]: boolean }>({});
  const [loadingFees, setLoadingFees] = useState<{ [key: string]: boolean }>({});
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [unitAppliances, setUnitAppliances] = useState<UnitAppliance[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchUnits();
  }, []);

  const fetchUnits = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get('api/Unit/all');

      if (response.ok) {
        const responseData = await response.json();
        console.log('Units Response:', responseData);

        let unitsList: Unit[] = [];

        if (responseData.success && responseData.data) {
          unitsList = responseData.data;
        } else if (Array.isArray(responseData)) {
          unitsList = responseData;
        }

        setUnits(unitsList);
        // Fetch fee configs for all units
        await fetchAllFeeConfigs(unitsList);
      } else {
        throw new Error('Failed to fetch units');
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      setError('Failed to load units. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAllFeeConfigs = async (unitsList: Unit[]) => {
    const fetchPromises = unitsList.map((unit) => fetchUnitFeeConfig(unit.id));
    const feeConfigs = await Promise.all(fetchPromises);

    setUnits((prevUnits) =>
      prevUnits.map((unit, index) => ({
        ...unit,
        unitFeeConfigs: feeConfigs[index] || []
      }))
    );
  };

  const fetchUnitFeeConfig = async (unitId: string): Promise<UnitFeeConfig[]> => {
    try {
      const response = await DataService.get(`api/UnitFeeConfig/unit/${unitId}`);

      if (response.ok) {
        const responseData = await response.json();
        console.log(`Fee Config for unit ${unitId}:`, responseData);

        if (responseData.success && responseData.data) {
          return Array.isArray(responseData.data) ? responseData.data : [responseData.data];
        } else if (Array.isArray(responseData)) {
          return responseData;
        }
      }
    } catch (error) {
      console.error(`Error fetching fee config for unit ${unitId}:`, error);
    }
    return [];
  };

  const handleFlipCard = (unitId: string) => {
    setFlippedCards((prev) => ({
      ...prev,
      [unitId]: !prev[unitId]
    }));
  };

  const getRandomImage = (index: number) => {
    return DUMMY_IMAGES[index % DUMMY_IMAGES.length];
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getPeriodPrice = (config: UnitFeeConfig, period: keyof Omit<UnitFeeConfig, 'id' | 'feeType' | 'feeTypeId'>) => {
    const value = config[period];
    return value > 0 ? formatCurrency(value) : null;
  };

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false);
    fetchUnits();
  };

  const fetchUnitAppliances = async (unitId: string): Promise<UnitAppliance[]> => {
    try {
      const response = await DataService.get(`api/UnitAppliance/unit-list/${unitId}`);

      if (response.ok) {
        const responseData = await response.json();
        console.log(`Appliances for unit ${unitId}:`, responseData);

        if (responseData.success && responseData.data) {
          return Array.isArray(responseData.data) ? responseData.data : [responseData.data];
        } else if (Array.isArray(responseData)) {
          return responseData;
        }
      }
    } catch (error) {
      console.error(`Error fetching appliances for unit ${unitId}:`, error);
    }
    return [];
  };

  const handleViewDetails = async (unit: Unit) => {
    setSelectedUnit(unit);
    setDetailsDialogOpen(true);
    setLoadingDetails(true);

    // Fetch appliances for this unit
    const appliances = await fetchUnitAppliances(unit.id);
    setUnitAppliances(appliances);
    setLoadingDetails(false);
  };

  const handleCloseDetails = () => {
    setDetailsDialogOpen(false);
    setSelectedUnit(null);
    setUnitAppliances([]);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box>
        {/* Header */}
        <Box
          sx={{
            mb: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <HomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            <Box>
              <Typography variant="h3" component="h1" fontWeight="bold">
                Rental Units
              </Typography>
              <Typography variant="body1" color="text.secondary">
                {units.length} {units.length === 1 ? 'unit' : 'units'} available
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              disabled={loading}
            >
              Add Unit
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchUnits}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 4 }} icon={<FlipIcon />}>
          Click the flip icon on any card to view fee configurations
        </Alert>

        {/* Units Grid */}
        {units.length === 0 ? (
          <Paper
            sx={{
              p: 8,
              textAlign: 'center',
              bgcolor: 'grey.50'
            }}
          >
            <HomeIcon sx={{ fontSize: 80, color: 'grey.300', mb: 2 }} />
            <Typography variant="h5" color="text.secondary">
              No units found
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={4}>
            {units.map((unit, index) => (
              <Grid item xs={12} sm={6} md={4} key={unit.id}>
                <FlipCardContainer>
                  <FlipCardInner flipped={!!flippedCards[unit.id]}>
                    {/* Front Face */}
                    <FlipCardFront>
                      <Box sx={{ position: 'relative' }}>
                        <StyledCardMedia
                          image={getRandomImage(index)}
                          title={`Unit ${unit.number}`}
                        />
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            zIndex: 1,
                            display: 'flex',
                            gap: 1
                          }}
                        >
                          <Tooltip title="View unit details">
                            <IconButton
                              onClick={() => handleViewDetails(unit)}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.95)',
                                boxShadow: 2,
                                '&:hover': {
                                  bgcolor: 'white',
                                  boxShadow: 4
                                }
                              }}
                              size="medium"
                            >
                              <VisibilityIcon color="primary" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View fee configurations">
                            <IconButton
                              onClick={() => handleFlipCard(unit.id)}
                              sx={{
                                bgcolor: 'rgba(255,255,255,0.95)',
                                boxShadow: 2,
                                '&:hover': {
                                  bgcolor: 'white',
                                  boxShadow: 4
                                }
                              }}
                              size="medium"
                            >
                              <AttachMoneyIcon color="primary" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            zIndex: 1
                          }}
                        >
                          <Chip
                            label={`Unit ${unit.number}`}
                            color="primary"
                            sx={{
                              fontWeight: 'bold',
                              fontSize: '1rem',
                              boxShadow: 2
                            }}
                          />
                        </Box>
                      </Box>

                      <CardContent sx={{ flexGrow: 1, p: 2, overflow: 'auto' }}>
                        {/* Property Info */}
                        <Box sx={{ mb: 2, minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            sx={{ mb: 0.5, minWidth: 0 }}
                          >
                            <BusinessIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                            <Typography
                              variant="subtitle1"
                              fontWeight="600"
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                flex: 1
                              }}
                              title={unit.property.code}
                            >
                              {unit.property.code}
                            </Typography>
                          </Stack>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              pl: 3,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block'
                            }}
                            title={unit.property.contact}
                          >
                            {unit.property.contact}
                          </Typography>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        {/* Unit Details */}
                        <Grid container spacing={1.5}>
                          <Grid item xs={6}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                p: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1
                              }}
                            >
                              <MeetingRoomIcon fontSize="small" color="primary" />
                              <Box>
                                <Typography variant="body1" fontWeight="600">
                                  {unit.rooms}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  Rooms
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={6}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                p: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1
                              }}
                            >
                              <BathtubIcon fontSize="small" color="primary" />
                              <Box>
                                <Typography variant="body1" fontWeight="600">
                                  {unit.bathrooms}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  Bathrooms
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={6}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                p: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1
                              }}
                            >
                              <SquareFootIcon fontSize="small" color="primary" />
                              <Box>
                                <Typography variant="body1" fontWeight="600">
                                  {unit.areaM2}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  Area (m²)
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          <Grid item xs={6}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                p: 1,
                                bgcolor: 'grey.50',
                                borderRadius: 1
                              }}
                            >
                              <HomeIcon fontSize="small" color="primary" />
                              <Box>
                                <Typography variant="body1" fontWeight="600">
                                  {unit.floor}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  Floor
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* Fee Config Count */}
                        {unit.unitFeeConfigs.length > 0 && (
                          <Box sx={{ mt: 2, textAlign: 'center' }}>
                            <Chip
                              icon={<AttachMoneyIcon />}
                              label={`${unit.unitFeeConfigs.length} Fee Configuration${unit.unitFeeConfigs.length !== 1 ? 's' : ''}`}
                              color="success"
                              variant="outlined"
                              sx={{ fontWeight: 500 }}
                            />
                          </Box>
                        )}
                      </CardContent>
                    </FlipCardFront>

                        {/* Back Face - Fee Configurations */}
                        <FlipCardBack>
                          <Box
                            sx={{
                              bgcolor: 'primary.main',
                              color: 'white',
                              p: 2.5,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              position: 'relative',
                              zIndex: 100
                            }}
                          >
                            <AttachMoneyIcon />
                            <Typography variant="h6" fontWeight="bold">
                              Fee Configurations
                            </Typography>
                            <IconButton
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                handleFlipCard(unit.id);
                              }}
                              sx={{
                                position: 'absolute',
                                right: 12,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                bgcolor: 'rgba(255,255,255,0.95)',
                                boxShadow: 2,
                                zIndex: 1000,
                                pointerEvents: 'auto',
                                '&:hover': {
                                  bgcolor: 'white',
                                  boxShadow: 4
                                }
                              }}
                              size="medium"
                              title="Go back to unit details"
                            >
                              <FlipIcon color="primary" />
                            </IconButton>
                          </Box>

                          <CardContent
                            sx={{
                              flexGrow: 1,
                              overflow: 'auto',
                              p: 2.5,
                              bgcolor: 'grey.50',
                              maxHeight: '420px',
                              '&::-webkit-scrollbar': {
                                width: '8px'
                              },
                              '&::-webkit-scrollbar-track': {
                                bgcolor: 'grey.200',
                                borderRadius: '4px'
                              },
                              '&::-webkit-scrollbar-thumb': {
                                bgcolor: 'primary.main',
                                borderRadius: '4px',
                                '&:hover': {
                                  bgcolor: 'primary.dark'
                                }
                              }
                            }}
                          >
                            {unit.unitFeeConfigs.length === 0 ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  height: '100%',
                                  color: 'text.secondary'
                                }}
                              >
                                <AttachMoneyIcon sx={{ fontSize: 60, mb: 2, opacity: 0.3 }} />
                                <Typography variant="body2">
                                  No fee configurations
                                </Typography>
                              </Box>
                            ) : (
                              <Stack spacing={2.5}>
                                {unit.unitFeeConfigs.map((config, idx) => (
                                  <Paper
                                    key={config.id}
                                    elevation={3}
                                    sx={{
                                      p: 2.5,
                                      bgcolor: 'white',
                                      borderLeft: 5,
                                      borderColor: 'primary.main',
                                      borderRadius: 2
                                    }}
                                  >
                                    <Typography
                                      variant="subtitle1"
                                      fontWeight="bold"
                                      color="primary.main"
                                      sx={{
                                        mb: 0.5,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                      title={config.feeType.name}
                                    >
                                      {config.feeType.name}
                                    </Typography>

                                    <Typography
                                      variant="body2"
                                      color="text.secondary"
                                      sx={{
                                        mb: 2,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        minHeight: '40px'
                                      }}
                                      title={config.feeType.description}
                                    >
                                      {config.feeType.description}
                                    </Typography>

                                    <Grid container spacing={1.5}>
                                      {getPeriodPrice(config, 'daily') && (
                                        <Grid item xs={6}>
                                          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              Daily
                                            </Typography>
                                            <Typography variant="body2" fontWeight="600" color="primary.main">
                                              {getPeriodPrice(config, 'daily')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                      {getPeriodPrice(config, 'weekly') && (
                                        <Grid item xs={6}>
                                          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              Weekly
                                            </Typography>
                                            <Typography variant="body2" fontWeight="600" color="primary.main">
                                              {getPeriodPrice(config, 'weekly')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                      {getPeriodPrice(config, 'monthly') && (
                                        <Grid item xs={6}>
                                          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              Monthly
                                            </Typography>
                                            <Typography variant="body2" fontWeight="600" color="primary.main">
                                              {getPeriodPrice(config, 'monthly')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                      {getPeriodPrice(config, 'bimonthly') && (
                                        <Grid item xs={6}>
                                          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              Bimonthly
                                            </Typography>
                                            <Typography variant="body2" fontWeight="600" color="primary.main">
                                              {getPeriodPrice(config, 'bimonthly')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                      {getPeriodPrice(config, 'quarterly') && (
                                        <Grid item xs={6}>
                                          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              Quarterly
                                            </Typography>
                                            <Typography variant="body2" fontWeight="600" color="primary.main">
                                              {getPeriodPrice(config, 'quarterly')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                      {getPeriodPrice(config, 'semiAnnual') && (
                                        <Grid item xs={6}>
                                          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              Semi-Annual
                                            </Typography>
                                            <Typography variant="body2" fontWeight="600" color="primary.main">
                                              {getPeriodPrice(config, 'semiAnnual')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                      {getPeriodPrice(config, 'annual') && (
                                        <Grid item xs={6}>
                                          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              Annual
                                            </Typography>
                                            <Typography variant="body2" fontWeight="600" color="primary.main">
                                              {getPeriodPrice(config, 'annual')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                      {getPeriodPrice(config, 'oneTime') && (
                                        <Grid item xs={6}>
                                          <Box sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1 }}>
                                            <Typography variant="caption" color="text.secondary" fontWeight="500">
                                              One Time
                                            </Typography>
                                            <Typography variant="body2" fontWeight="600" color="primary.main">
                                              {getPeriodPrice(config, 'oneTime')}
                                            </Typography>
                                          </Box>
                                        </Grid>
                                      )}
                                    </Grid>
                                  </Paper>
                                ))}
                              </Stack>
                            )}
                          </CardContent>
                        </FlipCardBack>
                      </FlipCardInner>
                    </FlipCardContainer>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

      <UnitCreateStandaloneDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={handleCreateSuccess}
      />

      {/* Unit Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold', py: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <HomeIcon />
              Unit Details - {selectedUnit?.number}
            </Box>
            <IconButton
              onClick={handleCloseDetails}
              sx={{ color: 'white' }}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2, minHeight: '400px', maxHeight: '70vh', overflowY: 'auto' }}>
          {selectedUnit && (
            <Box>
              {/* Unit Basic Information */}
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ mb: 2 }}>
                  Basic Information
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BusinessIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">Property:</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedUnit.property.code}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <MeetingRoomIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">Rooms:</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedUnit.rooms}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <BathtubIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">Bathrooms:</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedUnit.bathrooms}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <SquareFootIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">Area:</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedUnit.areaM2} m²</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <HomeIcon color="primary" fontSize="small" />
                      <Typography variant="body2" color="text.secondary">Floor:</Typography>
                      <Typography variant="body2" fontWeight="600">{selectedUnit.floor}</Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Paper>

              {/* Fee Configurations */}
              <Paper sx={{ p: 2, mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AttachMoneyIcon />
                  Fee Configurations ({selectedUnit.unitFeeConfigs.length})
                </Typography>
                {selectedUnit.unitFeeConfigs.length === 0 ? (
                  <Alert severity="info">No fee configurations available for this unit.</Alert>
                ) : (
                  <Stack spacing={2}>
                    {selectedUnit.unitFeeConfigs.map((config) => (
                      <Paper
                        key={config.id}
                        elevation={2}
                        sx={{
                          p: 2,
                          bgcolor: 'grey.50',
                          borderLeft: 4,
                          borderColor: 'primary.main'
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ mb: 1 }}>
                          {config.feeType.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {config.feeType.description}
                        </Typography>
                        <Grid container spacing={1.5}>
                          {getPeriodPrice(config, 'daily') && (
                            <Grid item xs={6} sm={4}>
                              <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Daily</Typography>
                                <Typography variant="body2" fontWeight="600" color="primary.main">
                                  {getPeriodPrice(config, 'daily')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {getPeriodPrice(config, 'weekly') && (
                            <Grid item xs={6} sm={4}>
                              <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Weekly</Typography>
                                <Typography variant="body2" fontWeight="600" color="primary.main">
                                  {getPeriodPrice(config, 'weekly')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {getPeriodPrice(config, 'monthly') && (
                            <Grid item xs={6} sm={4}>
                              <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Monthly</Typography>
                                <Typography variant="body2" fontWeight="600" color="primary.main">
                                  {getPeriodPrice(config, 'monthly')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {getPeriodPrice(config, 'bimonthly') && (
                            <Grid item xs={6} sm={4}>
                              <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Bimonthly</Typography>
                                <Typography variant="body2" fontWeight="600" color="primary.main">
                                  {getPeriodPrice(config, 'bimonthly')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {getPeriodPrice(config, 'quarterly') && (
                            <Grid item xs={6} sm={4}>
                              <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Quarterly</Typography>
                                <Typography variant="body2" fontWeight="600" color="primary.main">
                                  {getPeriodPrice(config, 'quarterly')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {getPeriodPrice(config, 'semiAnnual') && (
                            <Grid item xs={6} sm={4}>
                              <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Semi-Annual</Typography>
                                <Typography variant="body2" fontWeight="600" color="primary.main">
                                  {getPeriodPrice(config, 'semiAnnual')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {getPeriodPrice(config, 'annual') && (
                            <Grid item xs={6} sm={4}>
                              <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">Annual</Typography>
                                <Typography variant="body2" fontWeight="600" color="primary.main">
                                  {getPeriodPrice(config, 'annual')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                          {getPeriodPrice(config, 'oneTime') && (
                            <Grid item xs={6} sm={4}>
                              <Box sx={{ bgcolor: 'white', p: 1, borderRadius: 1 }}>
                                <Typography variant="caption" color="text.secondary">One Time</Typography>
                                <Typography variant="body2" fontWeight="600" color="primary.main">
                                  {getPeriodPrice(config, 'oneTime')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Paper>

              {/* Appliances */}
              <Paper sx={{ p: 2 }}>
                <Typography variant="h6" fontWeight="bold" color="primary.main" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DevicesIcon />
                  Appliances ({unitAppliances.length})
                </Typography>
                {loadingDetails ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress />
                  </Box>
                ) : unitAppliances.length === 0 ? (
                  <Alert severity="info">No appliances available for this unit.</Alert>
                ) : (
                  <Grid container spacing={2}>
                    {unitAppliances.map((appliance) => (
                      <Grid item xs={12} sm={6} key={appliance.id}>
                        <Paper
                          elevation={1}
                          sx={{
                            p: 2,
                            bgcolor: 'grey.50',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <DevicesIcon color="primary" fontSize="large" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2" fontWeight="600">
                              {appliance.type?.name || 'Unknown Appliance'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Quantity: {appliance.quantity}
                            </Typography>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDetails} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default RentalUnitsListing;
